import Field from 'components/Form/Field';
import Icon from 'components/Icon';
import KeyValue from 'components/Layout/KeyValue';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import {H2} from 'components/Typography';
import Value from 'components/Value';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {Backend} from 'types/api';

const HELP_CENTER_PEPPOL_SEND_URL = 'https://help.makeleaps.jp/ja/'; // TODO
const HELP_CENTER_HOW_TO_REGISTER_URL = 'https://help.makeleaps.jp/ja/'; // TODO
const REGISTRATION_FORM_URL =
    'https://www.makeleaps.jp/wp-content/uploads/2025/01/MakeLeaps_Peppol送信利用申込書.docx';

export type PeppolSendBackendData = Backend<{
    registration: Registration;
}>;
type Registration = {
    companyAddress: string;
    companyName: string;
    corporateNumber: string;
    lastUpdated: string;
    peppolId: string;
    representative: string;
    representativeEmail: string;
    status: string;
    taxRegistrationNumber: string;
};

const PeppolSend = () => {
    const {
        data: {registration},
    } = useBackend<PeppolSendBackendData>();

    const helpCenterLink = (
        <Link
            aria-label="help-center"
            href={HELP_CENTER_PEPPOL_SEND_URL}
            isOpenInNewTab={true}
            key="help-center"
        >
            {t`Help Center (Japanese site)`}
        </Link>
    );

    const peppolOrgLink = (
        <Link
            aria-label="peppol-org"
            href="https://peppol.org/"
            isOpenInNewTab={true}
            key="peppol-org"
        >
            peppol.org
        </Link>
    );

    const digitalAgencyLink = (
        <Link
            aria-label="japanese-digital-agencys-digital-invoice-website"
            href="https://www.digital.go.jp/policies/electronic_invoice"
            isOpenInNewTab={true}
            key="japanese-digital-agencys-digital-invoice-website"
        >
            {t`Japanese Digital Agency's digital invoice website`}
        </Link>
    );

    const howToRegisterLink = (
        <Link
            aria-label="help-center"
            href={HELP_CENTER_HOW_TO_REGISTER_URL}
            isOpenInNewTab={true}
            key="help-center-how-to-register"
        >
            {t`Help Center (Japanese site)`}
        </Link>
    );
    const mailAddressLink = (
        <Link
            aria-label="contact"
            href={`mailto:success@makeleaps.com`}
            isOpenInNewTab={true}
            key="mail-address"
        >
            {t`success@makeleaps.com`}
        </Link>
    );

    return (
        <div className="mb-6 max-w-[738px] text-base">
            <NavigationTabs.Partner selection="integrations" />
            <SectionHeader
                subtitle={t`Registering with a Peppol Access Point will allow your account to send Digital Invoices via the Peppol Network.`}
                title={t`Peppol Access Point Registration`}
            />
            <p className="mt-6">
                {jt`MakeLeaps supports sending Digital Invoices on the Peppol* network. In order to access this functionality, do the following:`}
            </p>
            <ol className="mt-4 list-decimal">
                <li>{t`Register here with MakeLeaps' partner Peppol Access Point, operated by Fast Accounting Ltd**.`}</li>
                <li>{t`Turn on "Peppol Send" in Early Access to reveal Digital Invoice and Peppol options in the app. Note that to be able to send your Digital Invoices, you have to wait until the Peppol Registration is complete.`}</li>
            </ol>
            <p className="mt-4">{t`You can check your Peppol Registration status and details below.`}</p>
            <p className="mt-4">{jt`For more details about Peppol registration and Peppol Send, please see our ${helpCenterLink}.`}</p>
            <p className="mt-3 text-xs font-normal text-light-secondary">
                {jt`* Peppol is an international standard for the electronic exchange of business documents that has been adopted in Japan, Europe and elsewhere around the world. Learn more at ${peppolOrgLink} or at the ${digitalAgencyLink}.`}
            </p>
            <p className="text-xs font-normal text-light-secondary">
                {t`** Use of Peppol Send requires agreeing to Fast Accounting Co., Ltd's terms of service.`}
            </p>
            <hr />
            <H2>{t`Registration for New Users`}</H2>
            <ol className="list-decimal">
                <li>{jt`Check the requirements in our ${howToRegisterLink}`}</li>
                <li className="mt-4">{t`Download and fill out the registration form (Japanese only).`}</li>
            </ol>
            <LinkButton
                className="mb-2 mt-4"
                download
                href={REGISTRATION_FORM_URL}
                kind="secondary"
                size="base"
            >
                {t`Registration Form`}
                <Icon className="ml-2 flex-none" icon="download" size="base" />
            </LinkButton>
            <ol className="mt-4 list-decimal" start={3}>
                <li>{jt`Send the form to MakeLeaps Customer Success Team at ${mailAddressLink}`}</li>
            </ol>
            <hr />
            <H2>{t`Peppol Registration Details`}</H2>
            {registration ? (
                <>
                    <p>{t`The following is the information that MakeLeaps has registered with Fast Accounting Co., Ltd. The data should match Japanese government records for your Company. Please contact MakeLeaps Customer Success Team if any of this information is out of date.`}</p>
                    <KeyValue className="ml-4 mt-4">
                        <Field
                            label={t`Peppol Registration Status`}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.status,
                                }}
                            />
                        </Field>
                        <Field
                            label={t`Peppol Registration last updated`}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.lastUpdated,
                                }}
                            />
                        </Field>
                        <Field
                            label={t`Peppol ID`}
                            tooltip={{
                                message: t`Only shown when Registration Status is "Active"`,
                            }}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.peppolId,
                                }}
                            />
                        </Field>
                        <Field label={t`Registered Company Name`} type="value">
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.companyName,
                                }}
                            />
                        </Field>
                        <Field
                            label={t`Registered Company Address`}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.companyAddress,
                                }}
                            />
                        </Field>
                        <Field label={t`Corporate Number`} type="value">
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.corporateNumber,
                                }}
                            />
                        </Field>
                        <Field label={t`Tax Registration Number`} type="value">
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.taxRegistrationNumber,
                                }}
                            />
                        </Field>
                        <Field label={t`Peppol Representative`} type="value">
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.representative,
                                }}
                            />
                        </Field>
                        <Field
                            label={t`Peppol Representative Email`}
                            type="value"
                        >
                            <Value
                                value={{
                                    type: 'string',
                                    value: registration.representativeEmail,
                                }}
                            />
                        </Field>
                    </KeyValue>
                </>
            ) : (
                <p>{t`There is no registration information to display.`}</p>
            )}
        </div>
    );
};

export default PeppolSend;
